import { useCallback } from "react"
import type { ReactNode } from "react"
import ReactMarkdown from "react-markdown"

const termsMarkdown = `
# Summary of Terms of Use

The ADBEx License Builder is a free online tool that helps researchers and organisations create customised data sharing licenses. ADBEx is an independent research platform.

By using the License Builder, you agree to the following simple principles:

## 1. What you can do

- Use the License Builder to generate data sharing licenses for datasets you own or manage.
- Reproduce, display, and distribute the license text for legitimate research, institutional, or policy use.

## 2. What you must not do

- You must not use the License Builder or its outputs for illegal, unethical, or misleading purposes.
- You must not imply that ADBEx provides legal advice or is a party to your agreements.
- You must not use the ADBEx name or logo to suggest endorsement.
- You must not remove attribution, version, or disclaimer notices from the generated license.
- You must not modify, adapt or change the license text.

## 3. Our responsibilities

- We provide the License Builder "as is" - it is a free tool, not legal advice.
- ADBEx is not responsible for how the generated licenses are used or interpreted.
- Using the License Builder does not create any legal relationship between you, your organisation, or the operators of ADBEx.
- We only process the information you enter to generate your license and do not store or reuse your dataset content.

## 4. Important notes

- Laws always take precedence: nothing in the license or these terms overrides national law.
- The License Builder may be updated or withdrawn at any time, but licenses you have already created remain valid.
- By continuing to use the License Builder after updates to Terms and Conditions, you accept the revised Terms of Use.

---

# Terms and Conditions for Use of the License Builder by ADBEx (v0.1)

These Terms and Conditions govern the use of the License Builder by ADBEx, an online tool that assists users in creating customised data sharing licenses. The License Builder and associated materials are provided by the African Data and Biospecimen Exchange (ADBEx). Each clause below is followed by an italicised plain-language explanation for ease of interpretation. Users should review these Terms carefully before using the License Builder.

## 1. Legal Status of ADBEx

ADBEx provides the License Builder as part of its activities and does not constitute a separate legal entity. For the purposes of these Terms and Conditions, references to "ADBEx" refer only to the individuals and authorised maintainers responsible for operating and administering the License Builder. These Terms do not create or imply any wider institutional, organisational or legal status for ADBEx, nor do they transfer ownership or governance of the tool to any institution. Nothing in these Terms shall be interpreted as making ADBEx, its operators, or its maintainers a party to any agreement or dispute regarding the use, misuse, interpretation, or enforcement of any Data Sharing License generated using the License Builder provided by ADBEx.

*When we say "ADBEx" here, we mean only the people running the License Builder. ADBEx is not a legal entity, and we are not part of any agreements or disputes about how your licenses are used.*

## 2. Purpose and Scope

The License Builder enables users to generate data sharing licenses by selecting specific terms and conditions to apply to their datasets. ADBEx provides this service to promote responsible and equitable data sharing. ADBEx is not a party to any licenses created through the License Builder and does not mediate, enforce, or monitor their use.

*We provide the tool to help you make licenses, but we are not part of your agreements or responsible for enforcing them.*

## 3. Ownership and Intellectual Property

The License Builder, its interface, code, content, and associated documentation are the intellectual property of ADBEx. The ADBEx Data Sharing License text is made available for use under a Creative Commons Attribution 4.0 International (CC BY 4.0) license unless otherwise indicated. Users must retain attribution to ADBEx when reproducing or using the License Builder's outputs.

*ADBEx owns the tool and the license template but allows you to use it with attribution.*

## 4. Permitted Use

You may use the License Builder to generate and download licenses for datasets that you own or have the authority to license. You may reproduce, display, and distribute the generated license text for legitimate research, institutional, or policy purposes, provided attribution to ADBEx is maintained.

*You can use and share licenses you generate, but you must credit ADBEx.*

## 5. Restrictions

You may not use the License Builder or the generated license text to:
- engage in illegal, unethical, or misleading activities;
- suggest that ADBEx provides legal advice or acts as a party to your data sharing agreement;
- use the ADBEx name, logo, or trademarks in a way that implies endorsement; or
- remove attribution, disclaimers, or version information from the generated license text.

You may not modify, adapt or change the text of any license generated.

*Do not pretend we are your lawyers or partners, do not misuse our name, do not remove our credit lines, and do not alter the license after you have generated it.*

## 6. Disclaimers and Limitation of Liability

The License Builder and associated materials are provided "as is" without warranty of any kind, whether express or implied. ADBEx makes no representations regarding the legal adequacy, accuracy, or enforceability of any license generated through the License Builder. Users are solely responsible for ensuring that their generated licenses comply with applicable laws, institutional policies, and ethical standards. ADBEx accepts no liability for loss, damage, or claims arising from the use or inability to use the License Builder or the licenses it produces.

*We offer a free tool, not legal advice. Use it at your own risk.*

## 7. No Legal Relationship Created

Using the License Builder does not create a contract, partnership, agency, or legal relationship between ADBEx and the user, nor between ADBEx and any third party receiving a license generated through the License Builder.

*Using the tool does not make ADBEx your partner or co-licensor.*

## 8. Data Privacy and Security

The License Builder processes only the information entered by the user to generate a license. ADBEx does not store or reuse dataset content or identifying information beyond what is necessary to provide the service. By using the License Builder, users consent to this limited processing of their input data.

*We only process your input to make your license and do not share or permanently store it.*

## 9. Modification and Availability of the Service

ADBEx may update, suspend, or discontinue the License Builder or any of its components at any time without prior notice. Licenses already generated through the License Builder will remain valid under the terms selected at the time of generation.

*We might update or take down the tool later, but licenses you have already made stay valid.*

## 10. Governing Law and ADBEx's Non-Involvement

### Platform-level issues

These Terms and Conditions, and any disputes arising from access to or use of the ADBEx License Builder or the ADBEx platform itself, are governed by the laws of South Africa.

### Resource-level issues

Disputes relating to the use, interpretation, enforcement, or legal effect of any Data Sharing License generated using the License Builder must be resolved under the laws of the country where the relevant licensed data originated.

### ADBEx is not a party

ADBEx is not a party to, and has no responsibility or liability for, any agreements, disputes, or outcomes arising from the use, misuse, interpretation, enforcement, licensing or transfer of any data covered by a Data Sharing License generated using the License Builder. All such matters are solely between the data provider and the data user.

### Precedence of local law

If any part of these Terms conflicts with applicable national laws governing data or data sharing activities, those national laws take precedence.

*South African law applies to anything about using the License Builder itself. Any issues about a specific license you have generated follow the laws of the country where the licensed data originated. ADBEx is not involved in any agreements or disputes about how your licenses or data are used, and local laws always take priority.*

## 11. Relationship to Applicable Law

Nothing in these Terms or in any ADBEx Data Sharing License alters or replaces any applicable law or regulation. National or institutional laws governing data protection, confidentiality, intellectual property, or other legal rights and duties take precedence over the License and these Terms.

*If the law says something different, the law takes priority over the license.*

## 12. Changes to These Terms

ADBEx may revise these Terms and Conditions for using the License Builder periodically. Updates will be posted with a new version date. Continued use of the License Builder after such changes constitutes acceptance of the revised Terms.

*We may update these Terms from time to time, and by continuing to use the tool, you agree to the new version.*

## 13. Contact

Questions, feedback, or requests related to the License Builder or these Terms and Conditions should be directed to:
African Data and Biospecimen Exchange (ADBEx)  
Email: adbex@sanbi.ac.za  
Website: https://license-builder.adbex.or

These Terms and Conditions were last updated on [insert date]. They are provided for institutional review and may be adapted to comply with local legal requirements.

---

*For full details, please read the complete document:*  
*See the full Terms and Conditions [here](https://adbex.org).*
`

const getTextFromChildren = (children: ReactNode) => {
  if (Array.isArray(children)) {
    return children.map(child => (typeof child === "string" ? child : "")).join("")
  }
  return typeof children === "string" ? children : ""
}

export const TermsPage = () => {
  const handleAnchorClick = useCallback((href?: string) => {
    if (!href) return
    if (href !== "#important-notes") return
    if (typeof document === "undefined") return
    const target = document.getElementById("important-notes")
    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" })
  }, [])

  return (
    <section className="bg-muted/10 pt-14">
      <div className="mx-auto w-full max-w-4xl px-6 pb-12 pt-12 md:pt-16 lg:pt-20">
        <div className="prose prose-slate react-markdown terms-markdown prose-sm sm:prose-base lg:prose-lg text-pretty hyphens-auto break-words prose-h1:text-3xl sm:prose-h1:text-4xl lg:prose-h1:text-5xl prose-h2:text-2xl sm:prose-h2:text-3xl lg:prose-h2:text-4xl prose-h3:text-xl sm:prose-h3:text-2xl prose-p:leading-relaxed prose-li:leading-relaxed">
          <ReactMarkdown
            components={{
              h2: ({ children, ...props }) => {
                const text = getTextFromChildren(children)
                const normalized = text.toLowerCase().replace(/^[0-9]+\.?\s*/, "")
                const id = normalized === "important notes" ? "important-notes" : undefined
                return (
                  <h2 id={id} {...props}>
                    {children}
                  </h2>
                )
              },
              a: ({ href, children, ...props }) => (
                <a
                  {...props}
                  href={href}
                  onClick={event => {
                    if (href === "#important-notes") {
                      event.preventDefault()
                      handleAnchorClick(href)
                    }
                  }}
                >
                  {children}
                </a>
              )
            }}
          >
            {termsMarkdown}
          </ReactMarkdown>
        </div>
      </div>
    </section>
  )
}
